<template>
  <section
    v-if="accordionsList && accordionsList.length > 0"
    class="animated-accordion"
  >
    <div ref="animatedAccordion" class="animated-accordion__wrap">
      <!-- Accordion Media (Image or Video) -->
      <div class="animated-accordion__image-wrap show-for-large">
        <template v-if="matchMedia">
        <div
          v-for="(item, index) in accordionsList"
          :id="`video_id_${index}`"
          :key="index"
          class="animated-accordion__image-container"
          :class="{
            'visibility-hidden': active !== index,
            'visibility-visible': active === index,
          }"
          @click="clickedMedia($event, index)"
        >
          <template v-if="item.videoUrl">
            <video :key="item.videoUrl" playsInline autoPlay muted loop="true">
              <source :src="item.videoUrl" type="video/mp4" />
            </video>
          </template>
          <template v-else-if="item.image && item.image.responsiveImage">
            <WidgetPicture
              :class="
                item.image?.responsiveImage?.aspectRatio > 0.8
                  ? '--horizontal-ratio'
                  : '--vertical-ratio'
              "
              :image="{ custom: item.image?.responsiveImage }"
              :lazyLoad="true"
              :alt="
                item.image?.responsiveImage?.alt !== null
                  ? item.image.responsiveImage.alt
                  : item.title
              "
            />
          </template>
        </div>
        </template>
      </div>

      <!-- Accordion Text Content -->
      <div v-intersectable class="animated-accordion__accordion-wrap">
        <template v-for="(item, indexWrap) in accordionsList" :key="indexWrap">
          <div
            :id="`progress-accordion-${indexWrap}`"
            class="animated-accordion__progress-bar"
          >
            <div
              :id="`progress-bar-${indexWrap}`"
              max="1000"
              value="0"
              class="animated-accordion__progress-bar-highlight"
            ></div>
          </div>

          <div
            :id="`animated-accordion-card-${indexWrap}`"
            class="animated-accordion__accordion-text-wrap flip-card-id overflow-hidden"
            :class="{
              'is-clicked flex-filler': active === indexWrap,
              'max-h-09em opacity-02': active !== indexWrap,
            }"
          >
            <div
              class="animated-accordion__accordion-content animated-accordion-grid-cont"
              :class="{ 'is-clicked': active === indexWrap }"
            >
              <button
                :id="`animated-accordion-button-${indexWrap}`"
                class="animated-accordion__accordion-button"
                @click="clickedVideoBox($event, indexWrap)"
              >
                <div
                  v-if="showIndexes"
                  class="animated-accordion__accordion-button-index show-for-large"
                >
                  {{ indexWrap + 1 }}
                </div>
                <div>
                  <h3
                    class="animated-accordion__accordion-title title-m-4"
                    v-html="item.title"
                  />
                  <div class="animated-accordion__accordion-text-wrapper">
                    <div class="animated-accordion__image-wrap hide-for-large">
                      <template v-if="!matchMedia">
                      <div class="animated-accordion__image-container">
                        <template v-if="item.videoUrl">
                          <WidgetVideo v-if="active === indexWrap"
                            :preload="'metadata'"
                            :videoUrl="item.videoUrl"
                            :loop="true"
                            :muted="true"
                            :autoplay="true"
                            :playInViewport="true"
                            @pause="active !== indexWrap"
                          />
                        </template>
                        <template
                          v-else-if="item.image && item.image.responsiveImage"
                        >
                          <WidgetPicture
                            :class="
                              item.image?.responsiveImage?.aspectRatio > 0.8
                                ? '--horizontal-ratio'
                                : '--vertical-ratio'
                            "
                            :image="{ custom: item.image?.responsiveImage }"
                            :lazyLoad="true"
                            :alt="
                              item.image?.responsiveImage?.alt !== null
                                ? item.image.responsiveImage.alt
                                : item.title
                            "
                          />
                        </template>
                      </div>
                      </template>
                    </div>
                    <div
                      class="animated-accordion__accordion-text text-s-1"
                      v-html="item.text"
                    />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'AnimatedAccordion',
}
</script>

<script setup>
import { gsap } from 'gsap'

const emit = defineEmits(['update-locomotive'])

const props = defineProps({
  accordionsList: {
    type: Array,
    default: () => [],
  },
  showIndexes: {
    type: Boolean,
    default: true,
  },
})

const play = ref(false)
const active = ref(0)
const tl = ref(undefined)

const matchMedia = useHelpers().evalMatchMedia('(min-width: 1000px)')

// https://vuejs.org/guide/reusability/custom-directives#custom-directives
const vIntersectable = {
  created(el, binding, vnode, prevVnode) {
    const options = {
      threshold: [0, 1],
    }

    const observer = new IntersectionObserver(
      (entries, observer) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // vnode.context.$emit('intersected')
            // vnode.context.play = true
            play.value = true
          } else {
            // vnode.context.play = false
            play.value = false
          }
        }),
      options
    )
    observer.observe(el)
  },
}

watch(
  () => play.value,
  (val) => {
    if (val) {
      initAnimation()
    } else if (tl.value && tl.value.isActive()) {
      tl.value.pause()
    }
  }
)

onBeforeUnmount(() => {
  if (tl.value && tl.value.isActive()) {
    tl.value.kill()
  }
  tl.value = undefined
})

onMounted(() => {
  active.value = 0
  // Settiamo tutti i video dell'accordion all'inizio e paused
  const accordionMediaContainers = document.getElementsByClassName(
    'animated-accordion__image-container'
  )
  for (let i = 0; i < accordionMediaContainers.length; i++) {
    const actualVideoEl = accordionMediaContainers[i]?.querySelector('video')
    if (actualVideoEl) {
      actualVideoEl.currentTime = 0
      actualVideoEl.pause()
    }
  }
})

const initAnimation = () => {
  const defButton = document.getElementById('animated-accordion-button-0')
  defButton.click()
}

function fix(item) {
  const thePromise = item.play()

  if (thePromise != undefined) {
    thePromise.then(function (_) {
      item.pause()
      item.currentTime = 0
    })
  }
}

const playPause = (video) => {
  if (!video) return
  const isPlaying =
    video.currentTime > 0 &&
    !video.paused &&
    !video.ended &&
    video.readyState > video.HAVE_CURRENT_DATA

  if (!isPlaying) {
    video.play()
  } else {
    video.pause()
  }
}

const playPauseTimeline = (timeline) => {
  if (timeline) {
    if (timeline.isActive()) {
      timeline.pause()
    } else {
      timeline.play()
    }
  }
}

const goOn = (els, index) => {
  tl.value.revert()

  if (index === els.length - 1) {
    initAnimation() // Riparte dal primo el dell'accordion
  } else {
    els[index + 1].querySelector('button').click()
  }
  emit('update-locomotive')
}

const clickedVideoBox = (event, num) => {
  const index = num
  const a = document.getElementsByClassName(
    'animated-accordion__accordion-text-wrap'
  )
  const b = document.getElementsByClassName(
    'animated-accordion__image-container'
  )
  const vidElement = document.getElementById(`video_id_${index}`)
  const vid = vidElement?.querySelector('video')

  const progress = document.getElementById('progress-bar-' + index)
  let timeline
  let animDuration
  const standardDurationInSeconds = 10

  // se elemento attuale è video cerco di farlo partire
  if (vid) {
    // playPause(vid)
    // animDuration = vid.duration
    const isPlaying =
      vid.currentTime > 0 &&
      !vid.paused &&
      !vid.ended &&
      vid.readyState > vid.HAVE_CURRENT_DATA

    if (!isPlaying) {
      vid.play()
    }
    animDuration = standardDurationInSeconds
  } else {
    animDuration = standardDurationInSeconds
  }

  // Mettiamo in pausa tutti i media non relativi all'elemento corrente
  for (let i = 0; i < a.length; i++) {
    if (i !== index) {
      const actualElementVideo = b[i]?.querySelector('video')
      if (actualElementVideo) {
        actualElementVideo.currentTime = 0
        actualElementVideo.pause()
      }
    }
  }

  // caso di riclick sullo stesso el
  if (tl.value && index === active.value) {
    playPauseTimeline(tl.value)
  } else {
    if (tl.value) {
      tl.value.revert()
    }
    active.value = index

    timeline = gsap.timeline({
      totalDuration: animDuration,
      ease: 'power2.out',
      paused: true,
      onComplete() {
        gsap.to(progress, {
          width: 0,
          duration: 0,
          ease: 'none',
        })
        goOn(a, index)
      },
    })

    timeline.fromTo(
      progress,
      { width: 0 },
      { width: '100%', duration: animDuration }
    )

    tl.value = timeline
    tl.value.play()
    emit('update-locomotive')
  }
}

const clickedMedia = (event, num) => {
  const index = num

  // caso di riclick sullo stesso el
  if (tl.value && index === active.value) {
    playPauseTimeline(tl.value)
  }
}
</script>

<style lang="scss">
@import './style.scss';
</style>
